import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_RETURN_WINDOW_DAYS, cachedStoreDetails, fetchStoreDetails } from '../utils/storeDetails';
import { cachedShippingZones, fetchShippingZones, uaeDeliveryFacts } from '../utils/deliveryZones';
import { DEFAULT_VAT_RATE, withVat } from '../utils/vat';

/**
 * Shop facts every page states: whether the store is VAT-registered and its
 * rate (VAT-inclusive prices and "incl. VAT" wording only while registered),
 * the return window and the delivery zones (GET /settings/public/store and
 * GET /settings/shipping/zones), loaded once per visit. Without the provider
 * (tests, isolated renders), and until the first answer, the store counts as
 * not registered (rate 0: prices = catalogue prices, no VAT wording) and no
 * zones are known, so delivery copy stays generic instead of guessing. A
 * cached answer (utils/factsCache, ≤ 10 min) paints first, so a registered
 * store's prices do not jump on the next page load.
 */
export const DEFAULT_STORE_FACTS = Object.freeze({
  vatRegistered: false,
  // False until a server answer (fresh or cached) says whether the store is
  // VAT-registered: checkout confirms it before placing an order.
  vatKnown: false,
  vatRate: DEFAULT_VAT_RATE,
  returnWindowDays: DEFAULT_RETURN_WINDOW_DAYS,
  returnFees: null,
  foundedYear: null,
  city: null,
  sameAs: [],
  zones: [],
  zonesLoaded: false,
});

const StoreFactsContext = createContext(DEFAULT_STORE_FACTS);
// Reads the VAT status again (bypassing the shared answer) and stores it;
// resolves to the normalised details, or null when the read failed.
const RefreshVatContext = createContext(async () => null);

const detailsFacts = (details) => ({
  // A failed read says nothing about VAT: keep what the page already shows.
  ...(details.vatKnown ? { vatRegistered: details.vatRegistered, vatRate: details.vatRate, vatKnown: true } : {}),
  returnWindowDays: details.returnWindowDays,
  returnFees: details.returnFees,
  foundedYear: details.foundedYear,
  city: details.city,
  sameAs: details.sameAs,
});

// The last answers (≤ 10 min, utils/factsCache) paint at once; the fresh
// ones below replace them (stale-while-revalidate).
const cachedFacts = () => {
  const details = cachedStoreDetails();
  const zones = cachedShippingZones();
  return {
    ...(details ? detailsFacts(details) : {}),
    ...(zones ? { zones, zonesLoaded: true } : {}),
  };
};

export const StoreFactsProvider = ({ children, initialFacts }) => {
  const [facts, setFacts] = useState(() => {
    const start = { ...DEFAULT_STORE_FACTS, ...(initialFacts || cachedFacts()) };
    // Tests / callers may pass only a rate: registered when it is above 0.
    if (initialFacts && initialFacts.vatRegistered === undefined && initialFacts.vatRate !== undefined) {
      start.vatRegistered = Number(initialFacts.vatRate) > 0;
    }
    // Facts handed in by a caller (tests, isolated renders) are known.
    if (initialFacts && initialFacts.vatKnown === undefined) start.vatKnown = true;
    if (!start.vatRegistered) start.vatRate = DEFAULT_VAT_RATE;
    return start;
  });

  useEffect(() => {
    if (initialFacts) return undefined;
    let active = true;
    fetchStoreDetails().then((details) => {
      if (active) {
        setFacts((cur) => ({ ...cur, ...detailsFacts(details) }));
      }
    });
    fetchShippingZones().then((zones) => {
      if (active) setFacts((cur) => ({ ...cur, zones, zonesLoaded: true }));
    });
    return () => { active = false; };
  }, [initialFacts]);

  const refreshVat = useCallback(async () => {
    const details = await fetchStoreDetails(fetch);
    if (!details.vatKnown) return null;
    setFacts((cur) => ({ ...cur, ...detailsFacts(details) }));
    return details;
  }, []);

  return (
    <RefreshVatContext.Provider value={refreshVat}>
      <StoreFactsContext.Provider value={facts}>{children}</StoreFactsContext.Provider>
    </RefreshVatContext.Provider>
  );
};

/** Re-reads the VAT status (checkout, before an order while it is unknown). */
export const useRefreshVat = () => useContext(RefreshVatContext);

export const useStoreFacts = () => useContext(StoreFactsContext);

/** The VAT rate for shop prices (0 while the store is not VAT-registered). */
export const useVatRate = () => useStoreFacts().vatRate;

/** True only while the store is VAT-registered: the shop may say "incl. VAT". */
export const useVatRegistered = () => useStoreFacts().vatRegistered;

/** uaeDeliveryFacts for the loaded zones, thresholds in AED as the shop shows them (null until known). */
export const useDeliveryFacts = () => {
  const { zones, vatRate } = useStoreFacts();
  return useMemo(() => uaeDeliveryFacts(zones, (amount) => withVat(amount, vatRate)), [zones, vatRate]);
};

export default StoreFactsContext;
